/**
 * TimeRangeChip · 冷热分离 v2 一期
 * ─────────────────────────────────
 * 低调"时间胶囊 + Popover 面板"组合控件,共享于 Job 列表 / 场景生成列表等
 * 页面的筛选栏:
 *   - 默认收起:一行灰色小字自解释当前时间窗("近 30 天"/"2026-09-10 → …")
 *   - 点击展开:Popover 卡片带顶部渐变横条,快捷预设按钮 + RangePicker,
 *     底部小字"3 个月前的数据已归档"呼应冷热分离设计
 *   - 视觉重量与"只看自己"checkbox 相当,不喧宾夺主
 *
 * 契约(v2:带 mode 的标签联合,让"相对预设"跟随现在滑动):
 *   - value?: TimeRangeValue | null
 *   - onChange?: (val: TimeRangeValue | null) => void
 *   直接接在 Ant Form.Item 或独立 useState 之下都行。
 *
 * 为什么是标签联合而不是绝对 [Dayjs, Dayjs]:
 *   绝对元组会在生成那一刻把"这是相对预设(近 N 天,应跟随现在)还是用户
 *   手选绝对区间(应固定)"这一信息压扁丢失。丢失后,窗口的 end 冻结在挂载
 *   时刻,之后新建的任务(partition_time = 创建时刻,晚于冻结 end)永远查不到。
 *   保留 mode,把 preset 的求值推迟到"发请求那一刻"(见 resolvePartitionRange),
 *   预设窗口即可整体滑到现在,手选绝对区间则原样固定。
 *
 * 冷热分离硬约束:disabledDate 兜住"最长 3 个月内"(与物理保留期对齐)。
 */
import { useState } from 'react';
import { Button, DatePicker, Popover, Space } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

// 冷热分离 v2 一期:时间快捷预设,收纳在 Popover 内。
// 每个预设是"最近 N 天",覆盖 UI 上 3 个高频档,兜底最长与物理保留期对齐。
export const TIME_RANGE_PRESETS: Array<{ label: string; days: number }> = [
  { label: '近 7 天', days: 7 },
  { label: '近 30 天', days: 30 },
  { label: '近 90 天', days: 90 },
];

/**
 * 时间窗口的取值:
 *   - preset:  相对预设"近 N 天",发请求时才解析成 [now-days, now],跟随现在滑动
 *   - absolute:用户在 RangePicker 手选的绝对区间,原样固定
 *   - null:    未限时间 / 不过滤
 */
export type TimeRangeValue =
  | { mode: 'preset'; days: number }
  | { mode: 'absolute'; range: [Dayjs, Dayjs] };

/**
 * 把 TimeRangeValue 解析成实际的 dayjs 区间。
 *   - preset:  每次调用现取 now,算出 [now-days, now](跟随现在)
 *   - absolute:原样返回用户选的区间
 * nowFn 可注入固定时钟,方便单测。给 chip 内 RangePicker 显示用。
 */
export const resolvePartitionRangeDayjs = (
  v: TimeRangeValue | null,
  nowFn: () => Dayjs = dayjs,
): { start: Dayjs; end: Dayjs } | null => {
  if (!v) return null;
  if (v.mode === 'preset') {
    const now = nowFn();
    return { start: now.subtract(v.days, 'day'), end: now };
  }
  return { start: v.range[0], end: v.range[1] };
};

/**
 * 把 TimeRangeValue 解析成后端字符串区间(本地无时区格式,与现状后端契约一致)。
 * 页面 handleSearch 发请求时用:preset 每次现算 [now-days, now],end 自动滑到当前,
 * 从而让"提交后刷新/手动查询"能查到刚创建的任务;absolute 原样格式化不覆盖。
 */
export const resolvePartitionRange = (
  v: TimeRangeValue | null,
  nowFn: () => Dayjs = dayjs,
): { start: string; end: string } | null => {
  const r = resolvePartitionRangeDayjs(v, nowFn);
  if (!r) return null;
  return {
    start: r.start.format('YYYY-MM-DDTHH:mm:ss'),
    end: r.end.format('YYYY-MM-DDTHH:mm:ss'),
  };
};

/**
 * 把 TimeRangeValue 压缩成"近 N 天"或"yyyy-mm-dd → yyyy-mm-dd",让 chip
 * 上单行文字自解释,不需要用户展开就能看清当前查询窗口。
 * 直接读 mode,不再靠"end 距 now 的时间差"启发式猜测。
 */
export const formatPartitionRangeSummary = (v: TimeRangeValue | null): string => {
  if (!v) return '未限时间';
  if (v.mode === 'preset') {
    const preset = TIME_RANGE_PRESETS.find(p => p.days === v.days);
    return preset ? preset.label : `近 ${v.days} 天`;
  }
  const [start, end] = v.range;
  return `${start.format('YYYY-MM-DD')} → ${end.format('YYYY-MM-DD')}`;
};

export interface TimeRangeChipProps {
  value?: TimeRangeValue | null;
  onChange?: (val: TimeRangeValue | null) => void;
}

const TimeRangeChip: React.FC<TimeRangeChipProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const normalized = value ?? null;
  const summary = formatPartitionRangeSummary(normalized);
  // preset 模式下按当前 now 解析出绝对区间供 RangePicker 显示(随 render 微动即
  // "跟随现在",符合预期);absolute 原样显示。
  const resolved = resolvePartitionRangeDayjs(normalized);

  // 冷热分离 v2 一期:3 个月前的数据已 DETACH 到冷备,列表页选不到。
  const disabledDate = (current: Dayjs) => {
    if (!current) return false;
    const now = dayjs();
    return (
      current.isAfter(now, 'day') ||
      current.isBefore(now.subtract(3, 'month'), 'day')
    );
  };

  const applyPreset = (days: number) => {
    // 只记 mode+days,不在点击时求值;发请求时(resolvePartitionRange)才解析,
    // 保证 end 跟随现在,而不是冻结在点击那一刻。
    onChange?.({ mode: 'preset', days });
    setOpen(false);
  };

  const panelContent = (
    <div style={{ width: 380, background: '#ffffff', borderRadius: 8, overflow: 'hidden' }}>
      {/*
        顶部细渐变横条 —— 让 Popover 有识别度,像"标题徽章",不像随便弹出
        来的默认下拉。宽度 3px,靠 60% 处收尾,视觉上暗示"从这里聚焦到内容"。
      */}
      <div
        style={{
          height: 3,
          background: 'linear-gradient(90deg, #1677ff 0%, #69b1ff 60%, transparent 100%)',
        }}
      />
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>创建时间</span>
          <span style={{ fontSize: 11, color: '#8c8c8c' }}>影响当前列表查询窗口</span>
        </div>
        <Space size={6} wrap style={{ marginBottom: 12 }}>
          {TIME_RANGE_PRESETS.map(p => {
            // active 判定直接读 mode,不再靠 summary 文案字符串相等。
            const active = normalized?.mode === 'preset' && normalized.days === p.days;
            return (
              <Button
                key={p.days}
                size="small"
                type={active ? 'primary' : 'default'}
                onClick={() => applyPreset(p.days)}
                style={{
                  borderRadius: 999,
                  paddingInline: 12,
                  fontWeight: active ? 500 : 400,
                }}
              >
                {p.label}
              </Button>
            );
          })}
        </Space>
        <DatePicker.RangePicker
          showTime
          value={resolved ? [resolved.start, resolved.end] : null}
          onChange={val => {
            // 用户手选立即标记为 absolute(原样固定);清空为 null(未限时间)。
            const next = (val && val[0] && val[1]
              ? { mode: 'absolute', range: [val[0], val[1]] }
              : null) as TimeRangeValue | null;
            onChange?.(next);
          }}
          disabledDate={disabledDate}
          style={{ width: '100%' }}
        />
        <div style={{ marginTop: 10, fontSize: 11, color: '#bfbfbf', lineHeight: 1.5 }}>
          超过 3 个月的数据已归档,列表不再展示
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={panelContent}
    >
      <Button
        size="small"
        type="text"
        icon={<CalendarOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
        style={{
          // 低调 chip:无底色、无描边,仅左右 padding + 淡灰文字,与筛选栏
          // 其他控件视觉重量相当。悬停/展开时字色轻微加深提示可点开。
          height: 28,
          paddingInline: 8,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          color: open ? '#1677ff' : '#595959',
          fontSize: 12,
          fontWeight: 400,
        }}
      >
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{summary}</span>
      </Button>
    </Popover>
  );
};

export default TimeRangeChip;
